import CryptoJS from "crypto-js";
import { useI18n } from 'vue-i18n'

export const createHash = (str) => {
    return CryptoJS.MD5(str).toString(CryptoJS.enc.Hex);
}

export const timestampToDayMonth = (timestamp) => {
    const date = new Date(timestamp);

    const dayName = new Intl.DateTimeFormat('en', { weekday: 'short' }).format(date);
    const monthName = new Intl.DateTimeFormat('en', { month: 'short' }).format(date);
    const dayOfMonth = date.getDate();

    return [dayName, dayOfMonth, monthName]
}

export const getDayShortName = (timestamp) => {
    const date = new Date(timestamp);
    const dayName = new Intl.DateTimeFormat('en', { weekday: 'short' }).format(date);

    return dayName
}

export const getDayNumber = (timestamp) => {
    const date = new Date(timestamp);
    const day = date.getDate()
    return String(day).padStart(2, '0');
}

export const timestampToDayMonthString = (timestamp) => {
    const date = new Date(timestamp);

    const dayName = new Intl.DateTimeFormat('en', { weekday: 'short' }).format(date);
    const monthName = new Intl.DateTimeFormat('en', { month: 'short' }).format(date);
    const dayOfMonth = date.getDate();

    return `${dayName} ${dayOfMonth} ${monthName}`
}

export const timestampToTime = (timestamp) => {
    const date = new Date(timestamp);

    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${hours}:${minutes}`
}

export const orderByGivenArray = (objects, orderArray, key) => {
    const orderMap = new Map(orderArray.map((value, index) => [value, index]));
  
    return objects.sort((a, b) => {
        const indexA = orderMap.get(a[key]) ?? Infinity;
        const indexB = orderMap.get(b[key]) ?? Infinity;
        return indexA - indexB;
    });
}

export const orderByLines = (odds, lineOrder, directionOrder) => {
    const oddsMap = new Map(
        odds.filter(o => o.st == 1).map((odd) => [`${odd.ln}-${odd.nm}`, odd])
    );
  
    const grid = lineOrder.map((line) => {
        const row = directionOrder.map((direction) => {
            return oddsMap.get(`${line}-${direction}`) || null;
        });
        return { line, row };
    }).filter((value) => value.row.some(e => e != null));
  
    return grid;
}

export const orderByScore = (odds) => {
    const categories = {
        homeWin: [],
        draw: [],
        awayWin: [],
    };
    
    // Categorize odds
    odds.forEach((odd) => {
        const [home, away] = odd.nm.split("-").map(Number);
        if (home > away) {
            categories.homeWin.push(odd);
        } else if (home === away) {
            categories.draw.push(odd);
        } else {
            categories.awayWin.push(odd);
        }
    });
    
      // Sort each category by score in the format:
      // home first, then away
    Object.keys(categories).forEach((key) => {
        categories[key].sort((a, b) => {
            const [homeA, awayA] = a.nm.split("-").map(Number);
            const [homeB, awayB] = b.nm.split("-").map(Number);
            return homeA - homeB || awayA - awayB;
        });
    });
    
    return categories;
}

export const toFixedTwo = (num) => {
    if (num == null || Number.isNaN(Number(num))) return "";
    const [integer, decimal = ""] = String(num).split(".");
    const trimmedDecimal = decimal.substring(0, 2)
    const paddedDecimal = trimmedDecimal.padEnd(2, "0")
    return `${integer}.${paddedDecimal}`;

}

// export const modelDateValue = () => {
//     return new Date().toISOString().split('T')[0]
// }
export const modelDateValue = () => {
    const now = new Date();
    const localDate = new Date(now.getTime() - (now.getTimezoneOffset() * 60000));
    return localDate.toISOString().split('T')[0];
};
// display helpers for the odds-format switcher: 'p' decimal, 'puk' fractional, 'pus' american
export const decimalToAmerican = (dec) => {
    const d = Number(dec)
    if (!Number.isFinite(d) || d <= 1) return ''
    const v = d >= 2 ? Math.round((d - 1) * 100) : Math.round(-100 / (d - 1))
    return v > 0 ? `+${v}` : `${v}`
}

// traditional UK bookmaker fractional ladder; converted odds snap to the nearest rung
const FRACTIONAL_LADDER = [
    [1,50],[1,33],[1,25],[1,20],[1,16],[1,14],[1,12],[1,10],[1,9],[1,8],[1,7],[2,13],[1,6],[2,11],[1,5],
    [2,9],[1,4],[2,7],[3,10],[1,3],[4,11],[2,5],[4,9],[1,2],[8,15],[4,7],[3,5],[8,13],[2,3],[7,10],[8,11],
    [4,5],[5,6],[10,11],[1,1],[21,20],[11,10],[6,5],[5,4],[11,8],[7,5],[3,2],[8,5],[13,8],[7,4],[9,5],
    [15,8],[2,1],[17,8],[9,4],[12,5],[5,2],[13,5],[11,4],[3,1],[10,3],[7,2],[4,1],[9,2],[5,1],[11,2],
    [6,1],[13,2],[7,1],[15,2],[8,1],[17,2],[9,1],[10,1],[11,1],[12,1],[14,1],[16,1],[18,1],[20,1],[22,1],
    [25,1],[28,1],[33,1],[40,1],[50,1],[66,1],[80,1],[100,1],[150,1],[200,1],[250,1],[500,1],[1000,1],
]

export const decimalToFractional = (dec) => {
    const d = Number(dec)
    if (!Number.isFinite(d) || d <= 1) return ''
    const x = d - 1
    if (x > 1000) return `${Math.round(x)}/1`
    let best = FRACTIONAL_LADDER[0], bestErr = Infinity
    for (const [n, den] of FRACTIONAL_LADDER) {
        const err = Math.abs(x - n / den)
        if (err < bestErr) { bestErr = err; best = [n, den] }
    }
    return `${best[0]}/${best[1]}`
}

// odd: full feed odd object when available (uses its exact puk/pus), or pass {p: <decimal>}
export const formatOdd = (odd, type = 'p') => {
    if (!odd) return ''
    if (type !== 'p' && odd[type] != null && odd[type] !== '') return odd[type]
    if (type === 'puk') return decimalToFractional(odd.p) || toFixedTwo(odd.p)
    if (type === 'pus') return decimalToAmerican(odd.p) || toFixedTwo(odd.p)
    return toFixedTwo(odd.p)
}

// finance/report pages can look back at most this many days
export const REPORT_MAX_AGE_DAYS = 180;
export const minReportDate = () => {
    const now = new Date();
    const localDate = new Date(now.getTime() - (now.getTimezoneOffset() * 60000) - (REPORT_MAX_AGE_DAYS * 86400000));
    return localDate.toISOString().split('T')[0];
};
export const clampToMinReportDate = (date) => {
    const min = minReportDate();
    return date && date < min ? min : date;
};
export const formatToLocalDate=(utcDateStr)=> {
    const localDate = new Date(utcDateStr); 
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, "0");
    const day = String(localDate.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
}
export function formatMarketName(market, event) {
  const { locale, messages } = useI18n()
  const currentLocale = locale.value
  const marketMessages = messages.value?.[currentLocale]?.markets || {}

  let marketName = marketMessages?.[market.i] || market.n
  if (!marketName) return ''

  marketName = marketName.replace(/\{home\}/gi, event?.h || '')
  marketName = marketName.replace(/\{away\}/gi, event?.a || '')

  const numbers = Array.isArray(market?.nrs) && market.nrs.length
    ? market.nrs
    : (Array.isArray(market?.params) ? market.params : [])

  const matches = [...marketName.matchAll(/\{([^}]+)\}/g)]
  matches.forEach((match, index) => {
    const key = match[1]
    const value = numbers[index]

    if (value !== undefined) {
      if (key.includes('number_ordinal')) {
        const n = Number(value)
        const suffix = (n % 10 === 1 && n % 100 !== 11)
          ? 'st'
          : (n % 10 === 2 && n % 100 !== 12)
          ? 'nd'
          : (n % 10 === 3 && n % 100 !== 13)
          ? 'rd'
          : 'th'
        marketName = marketName.replace(match[0], `${n}${suffix}`)
      } else {
        marketName = marketName.replace(match[0], value)
      }
    }
  })

  marketName = marketName.replace(/\{[^}]+\}/g, '').trim()

  return marketName
}

export const timezones = () => ({
    0: "GMT-12",
    1: "GMT-11",
    2: "GMT-10",
    3: "GMT-9",
    4: "GMT-8",
    5: "GMT-7",
    6: "GMT-6",
    7: "GMT-5",
    8: "GMT-4",
    9: "GMT-3",
    10: "GMT-2",
    11: "GMT-1",
    12: "GMT",
    13: "GMT+1",
    14: "GMT+2",
    15: "GMT+3",
    16: "GMT+4",
    17: "GMT+5",
    18: "GMT+6",
    19: "GMT+7",
    20: "GMT+8",
    21: "GMT+9",
    22: "GMT+10",
    23: "GMT+11",
    24: "GMT+12",
    25: "GMT+13",
})

export function debounce(fn, delay) {
    var timeoutID = null
        return function () {

        clearTimeout(timeoutID)
        var args = arguments
        var that = this
        timeoutID = setTimeout(function () {
            fn.apply(that, args)
        }, delay)
    }
}
export  const safeTranslate =(namespace, key, t, te)=> {
    const fullKey = `${namespace}["${key}"]`
    return te(fullKey) ? t(fullKey) : key
}

export function formatNumber(value) {
  if (value === null || value === undefined) return ''

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(value))
}

export function normalizeLang(value, fallback = 'en') {
    if (!value || typeof value !== 'string') return fallback
    const code = value.trim().toLowerCase().split('-')[0]
    return code || fallback
}
